import React from 'react';

function InvoiceAddressForm({ invoiceAddress, onChange, customerEmail, onEmailChange, emailError, onEmailBlur, orderItems = [], lastOrderNumbers = [], editableOrderNumber, setEditableOrderNumber }) {
    const debounceTimer = React.useRef();
    // Only watch address fields, not notes
    const addressFields = {
      institution: invoiceAddress.institution,
      adresszusatz: invoiceAddress.adresszusatz,
      name: invoiceAddress.name,
      street: invoiceAddress.street,
      city: invoiceAddress.city,
      postal_code: invoiceAddress.postal_code,
      email: customerEmail,
      recid: invoiceAddress.recid
    };
    const lastAddressValues = React.useRef({ ...addressFields });
    React.useEffect(() => {
      if (JSON.stringify(lastAddressValues.current) !== JSON.stringify(addressFields)) {
        lastAddressValues.current = { ...addressFields };
        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
          import('./api').then(api => {
            const token = window.sessionStorage.getItem('token');
            if (typeof api.updateInvoiceAddressInfo === 'function') {
              api.updateInvoiceAddressInfo({
                ...addressFields,
                token
              }).catch(() => {});
            } else {
              // Fallback: log warning if API not implemented
              console.warn('API updateInvoiceAddressInfo not implemented');
            }
          });
        }, 500);
      }
      return () => clearTimeout(debounceTimer.current);
    }, [addressFields]);
  // Prüfe, ob K1 oder M1 im Warenkorb ist
  const showLastOrderCard = orderItems.some(item => {
    const id = item?.product?.id || item?.product_id || '';
    return id && (id.startsWith('K1') || id.startsWith('M1'));
  });
  // // Debug-Ausgabe
  // if (typeof window !== 'undefined') {
  //   console.log('[InvoiceAddressForm] orderItems:', orderItems);
  //   console.log('[InvoiceAddressForm] showLastOrderCard:', showLastOrderCard);
  //   console.log('[InvoiceAddressForm] lastOrderNumbers:', lastOrderNumbers);
  // }



  // Sync: editableOrderNumber immer auf neuen Wert setzen, wenn sich lastOrderNumbers ändert
  React.useEffect(() => {
    if (Array.isArray(lastOrderNumbers) && lastOrderNumbers.length > 0) {
      setEditableOrderNumber(lastOrderNumbers[0].toString());
    } else {
      setEditableOrderNumber('');
    }
  }, [lastOrderNumbers, setEditableOrderNumber]);

  // Berechne die Gesamtmenge aller K1... und M1... Produkte
  const nummeriertMenge = orderItems
    .filter(item => {
      const id = item?.product?.id || item?.product_id || '';
      return id.startsWith('K1') || id.startsWith('M1');
    })
    .reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Berechne das Feld 'Nummeriert bis'
  let endNr = '';
  if (editableOrderNumber && nummeriertMenge > 0) {
    const start = parseInt(editableOrderNumber, 10);
    if (!isNaN(start)) {
      endNr = (start + nummeriertMenge - 1).toLocaleString('de-DE');
    }
  }

  // Formatierung mit Tausenderpunkten
  function formatNumberInput(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const strValue = value.toString();
  const num = strValue.replace(/\D/g, '');
  if (!num) return '';
  return parseInt(num, 10).toLocaleString('de-DE');
  }

  // Nummerierung von/bis wird jetzt von App.js berechnet und als Prop übergeben

  return (
    <>
      <div className="bg-white p-6 rounded-xl shadow-md mb-2">
        <h4 className="font-semibold text-gray-800 mb-3">Rechnungsadresse</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Firmenname Zeile 1</label>
            <input
              type="text"
              value={invoiceAddress.institution || ''}
              onChange={e => onChange({ ...invoiceAddress, institution: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Firmenname Zeile 1 (optional)"
              maxLength={60}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Firmenname Zeile 2</label>
            <input
              type="text"
              value={invoiceAddress.adresszusatz || ''}
              onChange={e => onChange({ ...invoiceAddress, adresszusatz: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Firmenname Zeile 2 (Adresszusatz, optional)"
              maxLength={60}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Empfänger</label>
            <input
              type="text"
              value={invoiceAddress.name}
              onChange={e => onChange({ ...invoiceAddress, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Empfängername"
              maxLength={60}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Straße</label>
            <input
              type="text"
              value={invoiceAddress.street}
              onChange={e => onChange({ ...invoiceAddress, street: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Straße und Hausnummer"
              maxLength={60}
              required
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">PLZ</label>
              <input
                type="text"
                value={invoiceAddress.postal_code}
                onChange={e => onChange({ ...invoiceAddress, postal_code: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="PLZ"
                maxLength={10}
                required
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Ort</label>
              <input
                type="text"
                value={invoiceAddress.city}
                onChange={e => onChange({ ...invoiceAddress, city: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ort"
                maxLength={40}
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ihre E-Mail-Adresse
              <span className="block text-xs text-gray-500 font-normal">An diese Adresse wird die Bestellbestätigung versendet</span>
            </label>
            <input
              type="email"
              value={customerEmail}
              onChange={e => onEmailChange(e.target.value)}
              onBlur={onEmailBlur}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${emailError ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="E-Mail Adresse"
              required
            />
            {emailError && (
              <div className="text-red-600 text-sm mt-1">{emailError}</div>
            )}
          </div>
        </div>
      </div>
      {/* Card für letzte Auftragsnummer, falls K1 oder M1 im Warenkorb */}
      {/* {showLastOrderCard && lastOrderNumbers && lastOrderNumbers.length > 0 && ( */}
      {showLastOrderCard && (
          <div className="bg-white p-6 rounded-xl shadow-md mb-2 mt-4">
            <h4 className="font-semibold text-gray-800 mb-3">Nummerierung</h4>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">von</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9.]+"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formatNumberInput(editableOrderNumber)}
                  onChange={e => {
                    const raw = e.target.value.replace(/\D/g, '');
                    setEditableOrderNumber(raw);
                  }}
                  placeholder="Nummeriert von"
                  maxLength={15}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">bis</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9.]+" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    value={endNr ? endNr.toLocaleString('de-DE') : ''}
                    readOnly
                    disabled
                    tabIndex={-1}
                    placeholder="Nummeriert bis"
                    maxLength={15}
                  />
              </div>
            </div>
          </div>
      )}
      {/* Separate card for notes below the invoice address */}
      <div className="bg-white p-6 rounded-xl shadow-md mb-2 mt-4">
        <h4 className="font-semibold text-gray-800 mb-3">Notizen zur Bestellung <span className="text-xs text-gray-500 font-normal">(optional)</span></h4>
        <textarea
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          rows={3}
          value={invoiceAddress.notes || ''}
          onChange={e => {
            const textarea = e.target;
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
            onChange({ ...invoiceAddress, notes: textarea.value });
          }}
          placeholder="Notizen zur Bestellung (optional)"
          style={{ overflow: 'hidden' }}
          onInput={e => {
            const textarea = e.target;
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
          }}
        />
      </div>
    </>
  );
}

export default InvoiceAddressForm;
